import type { IChat } from "@dreamcam/messenger-sdk";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/DropdownMenu";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { useDialogStore } from "@storage/global/useDialogStore";
import { useUnblockUserByIdMutation } from "@storage/queries/blacklist";
import { useUnmuteUserByIdMutation } from "@storage/queries/chat/mute";

import { DIALOG_KEYS } from "@services/DialogManager";

import { ICON_PATHS } from "@configs/icons";

interface ChatMenuProps {
	currentChat: IChat;
}

export const ChatMenu = ({ currentChat }: ChatMenuProps) => {
	const unBlockUserByIdMutation = useUnblockUserByIdMutation();
	const unmuteUserByIdMutation = useUnmuteUserByIdMutation();

	const { openDialog } = useDialogStore();
	const participantUserId = currentChat.participant.user_id;
	const participantUsername = currentChat.participant.username;

	const menuItems = [
		{
			key: 1,
			label: (
				<Typography size="sm" padding="none" color="error">
					{currentChat.participant.block_data.is_blocked ? "Unban" : "Ban"}
				</Typography>
			),
			icon: <Image width={24} height={24} src={ICON_PATHS.STOP} alt="stop" />,
			onClick: () => {
				if (currentChat.participant.block_data.is_blocked) {
					unBlockUserByIdMutation.mutate({ userId: currentChat.participant.user_id || "" });
				} else {
					if (!participantUserId || !participantUsername) return;

					openDialog(DIALOG_KEYS.BLOCK_USER, {
						userId: participantUserId,
						username: participantUsername,
					});
				}
			},
		},
		{
			key: 2,
			label: (
				<Typography size="sm" padding="none">
					{currentChat.participant.mute_data.is_muted ? "Unmute" : "Mute"}
				</Typography>
			),
			icon: <Image width={24} height={24} src={ICON_PATHS.MUTE} alt="mute" />,
			onClick: () => {
				if (currentChat.participant.mute_data.is_muted) {
					unmuteUserByIdMutation.mutate({ userId: currentChat.participant.user_id || "" });
				} else {
					if (!participantUserId || !participantUsername) return;

					openDialog(DIALOG_KEYS.MUTE_USER, {
						userId: participantUserId,
						username: participantUsername,
					});
				}
			},
		},
	];

	return (
		<DropdownMenu key="dropdown-menu-msg-chat" modal={false}>
			<DropdownMenuTrigger>
				<div
					className="flex cursor-pointer items-center gap-2.5"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
				>
					{currentChat.unread_count > 0 ? <div className="h-3 w-3 rounded-full bg-primary" /> : null}
					<Stack align="center" justify="center" className="h-7 w-7">
						<Image width={20} height={4} src={ICON_PATHS.DOTS} alt="dots" />
					</Stack>
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="min-w-40" id="dropdown-menu-msg-chat-content">
				{menuItems.map((item) => (
					<DropdownMenuItem key={item.key} onClick={item.onClick}>
						{item.icon}
						{item.label}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
